import { useState, PropsWithChildren } from "react";
import { ActivityIndicator, Text } from "react-native-paper";
import { Feather } from "@expo/vector-icons";
import { Pressable, TouchableOpacity, View } from "react-native";
import { gql, useMutation, useQuery } from "@apollo/client";
import { capitalize, sortBy } from "lodash";

import type { ScreenProps } from "./types";
import type { Address, PersonPhone, Employment, Person } from "../types";

import { LinkButton, ScreenContainer, ScrollableContainer, Modal } from "../../components";
import { CURRENT_MEMBER_FRAGMENT, CURRENT_PERSON_FRAGMENT, ADDRESS_FRAGMENT } from "../fragments";
import { useIntercom } from "../../hooks";
import { colors } from "../../../theme";
import config from "../../config";
import { EmailChangeNotice } from "./SettingsScreen";
import type { CurrentMember } from "../fragments";
import useSnackBar from "../../hooks/useSnackBar";
import EmploymentSection from "./EmploymentSection";
import DisplayAddress from "../../components/Address";
import { ContactChangedNotice } from "../EmploymentRemoveScreen";
import { useFeatureFlags } from "../../hooks/useFlagr";

export const ACCOUNT_EDIT_QUERY = gql`
  query currentMemberAccountEditQuery {
    currentMember {
      ...CurrentMemberFragment
    }
    currentPerson {
      ...CurrentPersonFragment
      employments {
        id
        title
        isPrimary
        facility {
          id
          name
          addresses {
            ...AddressFragment
          }
          section {
            id
            name
          }
        }
      }
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
  ${CURRENT_PERSON_FRAGMENT}
  ${ADDRESS_FRAGMENT}
`;

export const MUTATION = gql`
  mutation updatePersonContactAddress($id: ID!) {
    updatePersonContactAddress(id: $id) {
      message
      success
      person {
        ...CurrentPersonFragment
      }
    }
  }
  ${CURRENT_PERSON_FRAGMENT}
`;

const Divider = () => <View className="border-t border-sky-lighter" />;

const Unemployed = () => (
  <View className="p-6 rounded-lg border border-sky">
    <Text className="mb-1 text-small-tight text-ink-dark">Current Status</Text>
    <Text className="font-700 text-ink-darkest text-regular-tight">Unemployed</Text>
  </View>
);

export const navigationTitle = "My PGA Account";

export type Query = {
  currentMember: CurrentMember;
  currentPerson: Person;
};

const PersonPhone = ({ phone, label }: { phone?: PersonPhone; label: string }) => (
  <>
    <Text className="text-small-tight font-400 text-ink-darker">{label}</Text>
    {phone?.phoneFormatted ? (
      <Text className="text-regular-tight font-400 text-ink-darker mt-1">{phone.phoneFormatted}</Text>
    ) : (
      <Text className="text-regular-tight font-400 text-ink-darker mt-1">N/A</Text>
    )}
  </>
);

const TouchableAddress = ({
  onPress,
  address,
  children,
  isPreferredAddress,
}: PropsWithChildren<{
  onPress(address: Address): void;
  address: Address;
  isPreferredAddress: boolean;
}>) => {
  return (
    <TouchableOpacity activeOpacity={0.7} onPress={() => onPress(address)} testID={`address-touchable-${address.id}`}>
      <View className="px-6 py-4 flex-row">
        <View>
          {children}
          <DisplayAddress address={address} textClassName="text-small-normal" />
        </View>

        <View className="flex-1 flex-row justify-end items-center">
          {isPreferredAddress && <Feather name="check" size={24} color={colors.ink.darkest} testID="check-icon" />}
        </View>
      </View>
      <View className="border-t border-sky-light" />
    </TouchableOpacity>
  );
};
const EditContactPreferencesModal = ({
  visible,
  onDismiss,
  addresses,
  employments,
  onPress,
  contactAddress,
}: {
  visible: boolean;
  onDismiss(): void;
  addresses: Address[];
  employments: Employment[];
  onPress(address: Address): void;
  contactAddress?: Address;
}) => {
  const isPreferredAddress = (address: Address) => address.id === contactAddress?.id;

  const sortOrder = {
    home: 1,
    secondary: 2,
    main: 3,
    mailing: 4,
  };

  const sortAddresses = (addresses: Address[]) => sortBy(addresses, (address) => sortOrder[address.addressType]);

  return (
    <Modal {...{ visible, onDismiss, title: "Select Preferred Address" }}>
      <View className="mb-4">
        {sortAddresses(addresses).map((address, index) => (
          <TouchableAddress address={address} onPress={onPress} key={index} isPreferredAddress={isPreferredAddress(address)}>
            <Text className="text-regular-tight font-500 text-ink-darkest mb-2">{capitalize(address.addressType)} Address</Text>
          </TouchableAddress>
        ))}

        {employments.map(({ facility }) => {
          return sortAddresses(facility.addresses).map((address, index) => (
            <TouchableAddress address={address} onPress={onPress} key={index} isPreferredAddress={isPreferredAddress(address)}>
              <Text className="text-regular-tight font-500 text-ink-darkest">{facility.name}</Text>
              <Text className="text-regular-tight font-500 text-ink-darker mt-2">{capitalize(address.addressType)}:</Text>
            </TouchableAddress>
          ));
        })}
      </View>
    </Modal>
  );
};

export default function AccountEditScreen({ navigation }: ScreenProps<"AccountEdit">) {
  const [showEmailChangeNotice, setShowEmailChangeNotice] = useState(false);
  const { data, loading } = useQuery<Query>(ACCOUNT_EDIT_QUERY);

  const flags = useFeatureFlags();

  const [sendMutation] = useMutation(MUTATION);
  const showSnackBar = useSnackBar();
  const [editContactPreferencesModalVisible, setEditContactPreferencesModalVisible] = useState(false);
  function onDismissSetEditContactPreferencesModalVisible() {
    setEditContactPreferencesModalVisible(false);
  }

  function showEditContactPreferencesModal() {
    setEditContactPreferencesModalVisible(true);
  }

  const intercom = useIntercom();

  if (loading || !data) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }
  const { addresses, phoneNumbers, contactAddress, employments } = data.currentPerson;

  const homeAddress = addresses.find(({ addressType }) => addressType === "home");
  const secondaryAddress = addresses.find(({ addressType }) => addressType === "secondary");

  const homePhone = phoneNumbers.find(({ phoneType }) => phoneType === "home");
  const mobilePhone = phoneNumbers.find(({ phoneType }) => phoneType === "mobile");

  const { firstName, lastName, primaryEmail, memberClass } = data.currentMember;

  const navigateToAccountShow = () => navigation.navigate("AccountShow");
  const navigateToEmailChange = () => navigation.navigate("EmailChange");
  const navigateToAccountEditContact = () => navigation.getParent()?.navigate("AccountEditContact");
  const navigateToAddEmployment = () => navigation.navigate("AddEmployment");

  return (
    <ScreenContainer testID="AccountEditScreen" navigationTitle={navigationTitle} goBack={navigateToAccountShow}>
      <ContactChangedNotice onChangePreferences={showEditContactPreferencesModal} />
      <EmailChangeNotice
        visible={showEmailChangeNotice}
        onContinue={navigateToEmailChange}
        onDismiss={() => setShowEmailChangeNotice(false)}
      />
      <EditContactPreferencesModal
        {...{
          visible: editContactPreferencesModalVisible,
          onDismiss: onDismissSetEditContactPreferencesModalVisible,
          addresses,
          employments,
          contactAddress,
          onPress: async (address: Address) => {
            const {
              data: {
                updatePersonContactAddress: { success, message },
              },
            } = await sendMutation({
              variables: {
                id: address.id,
              },
            });

            if (success) {
              showSnackBar({ message: "Changes Saved", duration: 3000, icon: "check-circle" });
              onDismissSetEditContactPreferencesModalVisible();
            } else {
              const errorMsg = message || "Unexpected error, please try again.";
              showSnackBar({ message: errorMsg, duration: 3000, icon: "x-circle" });
              onDismissSetEditContactPreferencesModalVisible();
            }
          },
        }}
      />
      <ScrollableContainer>
        <View className="m-6">
          <View className="flex-row justify-between pb-4 items-center">
            <Text className="text-regular-tight font-700 text-ink-darkest">Name</Text>
            <Pressable className="active:bg-sky-light" onPress={() => intercom.present(config.intercomContent.accountEditName)}>
              <Feather name="info" size={24} color={colors.primary.DEFAULT} />
            </Pressable>
          </View>
          <Text className="text-regular-tight font-400 text-ink-darker">
            {firstName} {lastName}
          </Text>
        </View>

        <Divider />

        <View className="m-6">
          <View className="flex-row justify-between items-center pb-4">
            <Text className="text-regular-tight font-700 text-ink-darkest">Email</Text>
            <LinkButton testID="edit-email-button" onPress={() => setShowEmailChangeNotice(true)}>
              Edit
            </LinkButton>
          </View>
          <Text className="text-regular-tight font-400 text-ink-darker">{primaryEmail}</Text>
        </View>

        <Divider />

        <View className="m-6">
          <View className="flex-row justify-between items-center pb-4">
            <Text className="text-regular-tight font-700 text-ink-darkest">Home Address</Text>
            <LinkButton testID="edit-address-button" onPress={navigateToAccountEditContact}>
              Edit
            </LinkButton>
          </View>
          {!!homeAddress ? (
            <DisplayAddress address={homeAddress} />
          ) : (
            <Text className="text-regular-tight font-400 text-ink-darker">None</Text>
          )}
        </View>

        <View className="mx-6 my-3">
          <View className="flex-row justify-between items-center">
            <Text className="text-regular-tight font-700 pb-4 text-ink-darkest" testID="secondary-address-header">
              Secondary Address
            </Text>
          </View>
          {!!secondaryAddress ? (
            <DisplayAddress address={secondaryAddress} />
          ) : (
            <Text className="text-regular-tight font-400 text-ink-darker">None</Text>
          )}
        </View>

        <View className="mx-6 mt-4 mb-6">
          <View className="flex-row justify-between items-center">
            <Text className="text-regular-tight font-700 pb-4 text-ink-darkest">Phone</Text>
          </View>
          <PersonPhone phone={homePhone} label="Home" />
          <View className="mt-6">
            <PersonPhone phone={mobilePhone} label="Mobile" />
          </View>
        </View>

        <Divider />

        <View className="m-6" testID="contact-preferences-view">
          <View className="flex-row justify-between items-center">
            <Text className="text-regular-tight font-700 text-ink-darkest">Contact Preferences</Text>
            <LinkButton testID="edit-contact-preference-button" onPress={showEditContactPreferencesModal}>
              Edit
            </LinkButton>
          </View>

          {contactAddress ? (
            <>
              <Text className="text-small-tight font-400 text-ink-darker mt-4">Send all correspondence to:</Text>
              <Text className="text-regular-tight font-400 text-ink-darker mt-2 mb-1" testID="contact-preferences-address">
                {contactAddress.addressable.__typename === "CRMFacility"
                  ? contactAddress.addressable.name
                  : `${capitalize(contactAddress.addressType)} Address`}
              </Text>
              <DisplayAddress address={contactAddress} />
            </>
          ) : (
            <Text className="text-regular-tight font-400 text-ink-darker mt-4">None</Text>
          )}
        </View>

        <Divider />
        <View className="m-6">
          <View className="flex-row justify-between items-center pb-4">
            <Text className="text-regular-tight font-700 text-ink-darkest">Employment</Text>
            {flags["membership-add-employment"] ? (
              <LinkButton testID="add-employment-button" onPress={navigateToAddEmployment}>
                Add
              </LinkButton>
            ) : null}
          </View>
          {employments.length ? (
            [...employments]
              .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
              .map((employment, key) => <EmploymentSection key={key} employment={employment} memberClass={memberClass} />)
          ) : (
            <Unemployed />
          )}
        </View>
      </ScrollableContainer>
    </ScreenContainer>
  );
}
